import {
  Show,
  createEffect,
  createSignal,
  onCleanup,
  type JSX,
} from "solid-js";
import { Portal } from "solid-js/web";
import { t } from "../i18n";

const CLOSE_MS = 240;
const DISMISS_PX = 96;
const DISMISS_VELOCITY = 0.55; // px/ms

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Accessible name when title is not shown */
  ariaLabel?: string;
  children: JSX.Element;
}

/**
 * Mobile bottom sheet: backdrop dim, slide-up panel, drag-handle pull to dismiss.
 * Stays mounted until exit animation finishes so close matches open smoothness.
 */
export default function BottomSheet(props: Props) {
  const [mounted, setMounted] = createSignal(false);
  const [closing, setClosing] = createSignal(false);
  const [dragging, setDragging] = createSignal(false);

  let panelEl: HTMLDivElement | undefined;
  let backdropEl: HTMLButtonElement | undefined;
  let handleEl: HTMLDivElement | undefined;

  let dragPointerId: number | null = null;
  let startY = 0;
  let currentY = 0;
  let lastY = 0;
  let lastT = 0;
  let velocity = 0; // px/ms, positive = downward
  let closeTimer: ReturnType<typeof setTimeout> | undefined;
  let enterClearTimer: ReturnType<typeof setTimeout> | undefined;

  createEffect(() => {
    if (props.open) {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = undefined;
      }
      resetInlineStyles();
      setClosing(false);
      setDragging(false);
      setMounted(true);
      // After enter keyframes finish, drop animation so transform is free for drag
      if (enterClearTimer) clearTimeout(enterClearTimer);
      enterClearTimer = setTimeout(() => {
        enterClearTimer = undefined;
        freeTransformForDrag();
      }, 300);
      return;
    }

    if (mounted() && !closing()) {
      beginCloseAnimation(currentY);
    }
  });

  createEffect(() => {
    if (!mounted() || closing()) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    onCleanup(() => {
      document.body.style.overflow = prev;
    });
  });

  createEffect(() => {
    if (!mounted() || closing()) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  onCleanup(() => {
    if (closeTimer) clearTimeout(closeTimer);
    if (enterClearTimer) clearTimeout(enterClearTimer);
  });

  function resetInlineStyles() {
    if (panelEl) {
      panelEl.style.animation = "";
      panelEl.style.transition = "";
      panelEl.style.transform = "";
    }
    if (backdropEl) {
      backdropEl.style.animation = "";
      backdropEl.style.transition = "";
      backdropEl.style.opacity = "";
    }
  }

  /** Enter animation uses `transform`; until cleared, drag can't move the panel. */
  function freeTransformForDrag() {
    if (!panelEl || closing()) return;
    panelEl.style.animation = "none";
    panelEl.style.transform = "translateY(0)";
  }

  function finishUnmount() {
    setMounted(false);
    setClosing(false);
    setDragging(false);
    currentY = 0;
    dragPointerId = null;
    resetInlineStyles();
  }

  function beginCloseAnimation(fromY = 0) {
    if (closing()) return;
    setClosing(true);
    setDragging(false);
    dragPointerId = null;

    const panel = panelEl;
    const backdrop = backdropEl;
    const height = panel?.getBoundingClientRect().height || 400;
    const start = Math.max(0, fromY);

    if (panel) {
      panel.style.animation = "none";
      panel.style.transition = "none";
      panel.style.transform = `translateY(${start}px)`;
      void panel.offsetHeight;
      panel.style.transition = `transform ${CLOSE_MS}ms cubic-bezier(0.4, 0, 0.7, 0.2)`;
      panel.style.transform = `translateY(${height + 32}px)`;
    }

    if (backdrop) {
      const currentOpacity = backdrop.style.opacity
        ? Number(backdrop.style.opacity)
        : 1;
      backdrop.style.animation = "none";
      backdrop.style.transition = "none";
      backdrop.style.opacity = String(
        Number.isFinite(currentOpacity) ? currentOpacity : 1,
      );
      void backdrop.offsetHeight;
      backdrop.style.transition = `opacity ${CLOSE_MS}ms ease-in`;
      backdrop.style.opacity = "0";
    }

    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      closeTimer = undefined;
      finishUnmount();
    }, CLOSE_MS + 20);
  }

  function requestClose() {
    if (!mounted() || closing()) return;
    props.onClose();
    beginCloseAnimation(currentY);
  }

  function applyDragOffset(dy: number) {
    currentY = dy;
    if (panelEl) {
      panelEl.style.transition = "none";
      panelEl.style.animation = "none";
      panelEl.style.transform = `translateY(${dy}px)`;
    }
    if (backdropEl) {
      backdropEl.style.transition = "none";
      backdropEl.style.animation = "none";
      backdropEl.style.opacity = String(Math.max(0.12, 1 - dy / 360));
    }
  }

  function snapBack() {
    setDragging(false);
    dragPointerId = null;
    currentY = 0;
    velocity = 0;

    if (panelEl) {
      panelEl.style.transition =
        "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)";
      panelEl.style.transform = "translateY(0)";
      const clear = () => {
        if (panelEl && !closing()) {
          panelEl.style.transition = "";
          // keep animation:none so transform stays controllable
          panelEl.style.animation = "none";
          panelEl.style.transform = "translateY(0)";
        }
      };
      panelEl.addEventListener("transitionend", clear, { once: true });
      setTimeout(clear, 320);
    }
    if (backdropEl && !closing()) {
      backdropEl.style.transition = "opacity 0.28s ease-out";
      backdropEl.style.opacity = "1";
      const clearBg = () => {
        if (backdropEl && !closing()) {
          backdropEl.style.transition = "";
          backdropEl.style.opacity = "";
        }
      };
      backdropEl.addEventListener("transitionend", clearBg, { once: true });
      setTimeout(clearBg, 320);
    }
  }

  function endDrag() {
    if (!dragging() || closing()) return;

    const shouldClose =
      currentY > DISMISS_PX ||
      (velocity > DISMISS_VELOCITY && currentY > 24);

    if (shouldClose) {
      const from = currentY;
      setDragging(false);
      dragPointerId = null;
      props.onClose();
      beginCloseAnimation(from);
    } else {
      snapBack();
    }
  }

  function onHandlePointerDown(e: PointerEvent) {
    if (closing() || e.button !== 0) return;
    // Only primary pointer
    if (dragPointerId != null) return;

    freeTransformForDrag();
    dragPointerId = e.pointerId;
    startY = e.clientY;
    lastY = e.clientY;
    lastT = performance.now();
    currentY = 0;
    velocity = 0;
    setDragging(true);

    try {
      handleEl?.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    e.preventDefault();
  }

  function onHandlePointerMove(e: PointerEvent) {
    if (dragPointerId !== e.pointerId || !dragging() || closing()) return;

    const now = performance.now();
    const dy = Math.max(0, e.clientY - startY);
    const dt = Math.max(1, now - lastT);
    velocity = (e.clientY - lastY) / dt;
    lastY = e.clientY;
    lastT = now;

    applyDragOffset(dy);
    e.preventDefault();
  }

  function onHandlePointerUp(e: PointerEvent) {
    if (dragPointerId !== e.pointerId) return;
    try {
      handleEl?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    endDrag();
  }

  function onHandlePointerCancel(e: PointerEvent) {
    if (dragPointerId !== e.pointerId) return;
    try {
      handleEl?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!closing()) snapBack();
  }

  return (
    <Show when={mounted()}>
      <Portal>
        <div
          class={`bottom-sheet-root fixed inset-0 z-[90] flex items-end justify-center${
            closing() ? " bottom-sheet-root--closing" : ""
          }${dragging() ? " bottom-sheet-root--dragging" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={props.ariaLabel ?? props.title ?? t("CREATE")}
        >
          <button
            ref={backdropEl}
            type="button"
            class="bottom-sheet-backdrop absolute inset-0 bg-black/65 backdrop-blur-[2px]"
            aria-label={t("CLOSE")}
            disabled={closing() || dragging()}
            onClick={() => requestClose()}
          />
          <div
            ref={panelEl}
            class="bottom-sheet-panel relative z-10 w-full max-w-lg rounded-t-2xl border border-surface-border border-b-0 bg-surface-raised shadow-card"
            style={{
              "padding-bottom":
                "max(0.75rem, env(safe-area-inset-bottom, 0px))",
            }}
          >
            {/* Drag handle — pull down to dismiss */}
            <div
              ref={handleEl}
              class="bottom-sheet-handle touch-none select-none"
              style={{ "touch-action": "none" }}
              role="separator"
              aria-label={t("CLOSE")}
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerCancel}
            >
              <div class="bottom-sheet-handle-pill" />
            </div>

            <Show when={props.title}>
              <h2 class="px-4 pb-2 text-center text-[13px] font-semibold text-ink-muted">
                {props.title}
              </h2>
            </Show>
            <div class="px-2 pb-2 pt-1">{props.children}</div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
